import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Breadcrumb,
    Tabs,
    Space,
    Button,
    Tag,
    Spin,
    message,
    Tooltip,
    Alert,
    Dropdown,
    Modal,
} from 'antd';
import {
    HomeOutlined,
    EditOutlined,
    PlusOutlined,
    TeamOutlined,
    BellOutlined,
    MoreOutlined,
    DeleteOutlined,
    ExportOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Case, CaseStatus, getCaseById, deleteCase } from '../../services/case';
import { getUserInfo } from '../../utils/auth';
import { getCaseCollaborators } from '../../services/case';
import CaseInfoTab from './components/CaseDetail/CaseInfoTab';
import CaseFollowUpTab from './components/CaseDetail/CaseFollowUpTab';
import CaseMilestoneTab from './components/CaseDetail/CaseMilestoneTab';
import CaseFinanceTab from './components/CaseDetail/CaseFinanceTab';
import CaseAttachmentTab from './components/CaseDetail/CaseAttachmentTab';
import CaseLeadTab from './components/CaseDetail/CaseLeadTab';
import InviteCollaboratorModal from './components/CaseDetail/InviteCollaboratorModal';
import SetReminderModal from './components/CaseDetail/SetReminderModal';
import './styles.css';

const { TabPane } = Tabs;

const CaseDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [caseData, setCaseData] = useState<Case | null>(null);
    const [activeTab, setActiveTab] = useState('info');
    const [currentUserId, setCurrentUserId] = useState<number | null>(null);
    const [isCollaborator, setIsCollaborator] = useState(false);
    const [inviteModalVisible, setInviteModalVisible] = useState(false);
    const [reminderModalVisible, setReminderModalVisible] = useState(false);
    
    // Track which tabs have been loaded for caching
    const [loadedTabs, setLoadedTabs] = useState<Set<string>>(new Set(['info']));

    // Load case details
    const loadCaseDetails = async () => {
        if (!id) return;

        setLoading(true);
        try {
            const data = await getCaseById(parseInt(id));
            setCaseData(data);
        } catch (error: any) {
            if (error.response?.status === 404) {
                message.error('案件不存在');
                navigate('/cases');
            } else if (error.response?.status === 403) {
                message.error('无权限访问此案件');
                navigate('/cases');
            } else {
                message.error('加载案件详情失败');
            }
        } finally {
            setLoading(false);
        }
    };

    // Load current user
    useEffect(() => {
        const userInfo = getUserInfo();
        if (userInfo) {
            setCurrentUserId(userInfo.id);
        }
    }, []);

    // Load case details on mount
    useEffect(() => {
        if (id) {
            loadCaseDetails();
        }
    }, [id]);

    // Check if user is collaborator
    useEffect(() => {
        const checkCollaborator = async () => {
            if (!caseData?.id || !currentUserId) {
                setIsCollaborator(false);
                return;
            }
            try {
                const collaborators = await getCaseCollaborators(caseData.id);
                const isCollab = collaborators.some(c => c.user_id === currentUserId);
                setIsCollaborator(isCollab);
            } catch (error) {
                console.error('Failed to check collaborator status', error);
                setIsCollaborator(false);
            }
        };
        checkCollaborator();
    }, [caseData?.id, currentUserId]);

    // Permission checks - use permissions from API response if available
    const permissions = caseData?.permissions;
    const canView = permissions?.can_view !== undefined ? permissions.can_view : true;
    const canEdit = permissions?.can_edit !== undefined ? permissions.can_edit : false;
    const canDelete = permissions?.can_delete !== undefined ? permissions.can_delete : false;
    const canInvite = permissions?.can_invite !== undefined ? permissions.can_invite : false;
    const canManageFinance = permissions?.can_manage_finance !== undefined ? permissions.can_manage_finance : false;

    // Handle delete case
    const handleDelete = () => {
        if (!caseData) return;

        Modal.confirm({
            title: '确认删除',
            content: `确定要删除案件"${caseData.case_name}"吗？此操作不可恢复。`,
            okText: '确认',
            okType: 'danger',
            cancelText: '取消',
            onOk: async () => {
                try {
                    await deleteCase(caseData.id);
                    message.success('删除成功');
                    navigate('/cases');
                } catch (error) {
                    message.error('删除失败');
                }
            },
        });
    };

    // Handle export case
    const handleExport = () => {
        message.info('导出功能开发中');
    };

    // More actions menu
    const moreMenuItems: MenuProps['items'] = [
        canDelete && {
            key: 'delete',
            label: '删除案件',
            icon: <DeleteOutlined />,
            danger: true,
            onClick: handleDelete,
        },
        {
            key: 'export',
            label: '导出案件',
            icon: <ExportOutlined />,
            onClick: handleExport,
        },
    ].filter(Boolean) as MenuProps['items'];

    // Get status color
    const getStatusColor = (status: CaseStatus): string => {
        const colorMap: Record<CaseStatus, string> = {
            [CaseStatus.ACTIVE]: 'blue',
            [CaseStatus.CLOSED]: 'default',
            [CaseStatus.SUSPENDED]: 'orange',
            [CaseStatus.PENDING]: 'gold',
            [CaseStatus.IN_PROGRESS]: 'processing',
            [CaseStatus.WAITING_CLIENT]: 'warning',
            [CaseStatus.WAITING_COURT]: 'cyan',
            [CaseStatus.COMPLETED]: 'success',
            [CaseStatus.CANCELLED]: 'error',
        };
        return colorMap[status] || 'default';
    };

    // Get status text
    const getStatusText = (status: CaseStatus): string => {
        const textMap: Record<CaseStatus, string> = {
            [CaseStatus.ACTIVE]: '进行中',
            [CaseStatus.CLOSED]: '已结案',
            [CaseStatus.SUSPENDED]: '已暂停',
            [CaseStatus.PENDING]: '待处理',
            [CaseStatus.IN_PROGRESS]: '进行中',
            [CaseStatus.WAITING_CLIENT]: '等待客户',
            [CaseStatus.WAITING_COURT]: '等待法院',
            [CaseStatus.COMPLETED]: '已完成',
            [CaseStatus.CANCELLED]: '已取消',
        };
        return textMap[status] || status;
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '100px 0' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!caseData) {
        return null;
    }

    return (
        <div className="case-detail-page">
            {/* Breadcrumb */}
            <Breadcrumb style={{ marginBottom: 16 }}>
                <Breadcrumb.Item>
                    <HomeOutlined />
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                    <a onClick={() => navigate('/cases')}>案件管理</a>
                </Breadcrumb.Item>
                <Breadcrumb.Item>案件详情</Breadcrumb.Item>
            </Breadcrumb>

            {/* Header */}
            <div className="case-detail-header">
                <div className="case-detail-header-top">
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', flex: 1, minWidth: 0 }}>
                        <div className="case-avatar">
                            {caseData.case_name?.charAt(0) || 'C'}
                        </div>
                        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                            <div style={{ marginBottom: '8px' }}>
                                <h2 className="case-title" style={{ display: 'inline', marginRight: '12px' }}>
                                    {caseData.case_name}
                                </h2>
                                <Tag color={getStatusColor(caseData.case_status)}>
                                    {getStatusText(caseData.case_status)}
                                </Tag>
                            </div>
                            <div className="case-meta">
                                <span>案件编号：{caseData.case_number}</span>
                                <span style={{ margin: '0 8px' }}>|</span>
                                <span>负责律师：{caseData.responsible_lawyer_name || '未指定'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <Space style={{ flexShrink: 0 }}>
                        {canInvite && (
                            <Tooltip title="邀请协作">
                                <Button
                                    icon={<TeamOutlined />}
                                    onClick={() => setInviteModalVisible(true)}
                                >
                                    邀请协作
                                </Button>
                            </Tooltip>
                        )}
                        {canEdit && (
                            <Tooltip title="设置提醒">
                                <Button
                                    icon={<BellOutlined />}
                                    onClick={() => setReminderModalVisible(true)}
                                >
                                    设置提醒
                                </Button>
                            </Tooltip>
                        )}
                        {moreMenuItems && moreMenuItems.length > 0 && (
                            <Dropdown menu={{ items: moreMenuItems }} placement="bottomRight">
                                <Button icon={<MoreOutlined />} />
                            </Dropdown>
                        )}
                    </Space>
                </div>

                {/* Permission Alert */}
                {!canEdit && canView && (
                    <Alert
                        message="只读权限"
                        description="您不是此案件的负责律师或助理律师，仅可查看。"
                        type="info"
                        showIcon
                        style={{ marginTop: 16 }}
                    />
                )}
            </div>

            {/* Tabs */}
            <div className="case-detail-content">
                <Tabs
                    activeKey={activeTab}
                    onChange={(key) => {
                        setActiveTab(key);
                        // Mark tab as loaded for caching
                        setLoadedTabs(prev => new Set(Array.from(prev).concat(key)));
                    }}
                    className="case-detail-tabs"
                >
                    <TabPane tab="案件信息" key="info">
                        <div className="case-detail-body">
                            <CaseInfoTab
                                caseData={caseData}
                                canEdit={canEdit}
                                onRefresh={loadCaseDetails}
                            />
                        </div>
                    </TabPane>

                    <TabPane tab="案件跟进" key="followup">
                        <div className="case-detail-body">
                            {/* Lazy load but cache once loaded */}
                            {loadedTabs.has('followup') && (
                                <CaseFollowUpTab
                                    caseId={caseData.id}
                                    canEdit={canEdit}
                                />
                            )}
                        </div>
                    </TabPane>

                    <TabPane tab="案件大事记" key="milestone">
                        <div className="case-detail-body">
                            {/* Lazy load but cache once loaded */}
                            {loadedTabs.has('milestone') && (
                                <CaseMilestoneTab
                                    caseId={caseData.id}
                                    canEdit={canEdit}
                                />
                            )}
                        </div>
                    </TabPane>

                    <TabPane tab="财务记录" key="finance">
                        <div className="case-detail-body">
                            {/* Lazy load but cache once loaded */}
                            {loadedTabs.has('finance') && (
                                <CaseFinanceTab
                                    caseId={caseData.id}
                                    caseData={caseData}
                                    canEdit={canManageFinance}
                                />
                            )}
                        </div>
                    </TabPane>

                    <TabPane tab="附件管理" key="attachment">
                        <div className="case-detail-body">
                            {/* Lazy load but cache once loaded */}
                            {loadedTabs.has('attachment') && (
                                <CaseAttachmentTab
                                    caseId={caseData.id}
                                    canEdit={canEdit}
                                />
                            )}
                        </div>
                    </TabPane>

                    <TabPane tab="关联线索" key="lead">
                        <div className="case-detail-body">
                            {/* Lazy load but cache once loaded */}
                            {loadedTabs.has('lead') && (
                                <CaseLeadTab leadId={caseData.lead_id || null} />
                            )}
                        </div>
                    </TabPane>
                </Tabs>
            </div>

            {/* Invite Collaborator Modal */}
            {inviteModalVisible && (
                <InviteCollaboratorModal
                    visible={inviteModalVisible}
                    caseId={caseData.id}
                    caseData={caseData}
                    onCancel={() => setInviteModalVisible(false)}
                    onSuccess={() => {
                        setInviteModalVisible(false);
                        loadCaseDetails();
                    }}
                />
            )}

            {/* Set Reminder Modal */}
            {reminderModalVisible && (
                <SetReminderModal
                    visible={reminderModalVisible}
                    targetType="case"
                    targetId={caseData.id}
                    onCancel={() => setReminderModalVisible(false)}
                    onSuccess={() => {
                        setReminderModalVisible(false);
                        message.success('提醒设置成功');
                    }}
                />
            )}
        </div>
    );
};

export default CaseDetail;
